import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Editor, type ImportHandler } from '../../features/editor/Editor';
import type { MainMenuHome } from '../../features/editor/MainMenu';
import type { PresenceProps } from '../../features/presence/PresenceLayer';
import type { CommentsProps } from '../../features/comments/CommentsLayer';
import type { AuthorsSource } from '../../features/editor/authors';
import { BoardOpenError, openLocalBoard, type LocalBoard } from '../../features/editor/persistence';
import type { MediaResolver } from '../../features/editor/lod';
import { importAssets, importImages } from '../../features/media/import';
import type { BoardMediaSession } from '../../features/media/session';
import type { LinkServices } from '../../features/links/services';
import { LocalMediaStore } from '../../features/media/store';
import { UploadPanel } from '../../features/media/UploadPanel';
import { LOCAL_CAPABILITIES, type Capabilities } from '../../features/sharing/capabilities';
import { Restricted } from '../../features/sharing/Restricted';
import { exporterFor } from '../../features/export/ExportDialog';
import { TopBar, TopBarProvider } from '../../features/editor/TopBar';
import { TitlePill } from '../../features/editor/TitlePill';
import { navigate } from '../router';
import { useLayout } from '../layout';
import { usePaletteCommands } from '../../features/palette/registry';
import { BoardSkeleton, ErrorState } from '../../ui/states';
import { Button } from '../../ui/Button';
import { PaletteRegistry } from '../../features/palette/registry';
import type { Api } from '../../lib/api';

type State =
  | { status: 'loading' }
  | { status: 'ready'; board: LocalBoard; media: MediaResolver; local: LocalMediaStore; onImport: ImportHandler; session: BoardMediaSession | null; links: LinkServices | null }
  | { status: 'error'; error: unknown };

const prefersReducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export function download(name: string, data: unknown): void {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Board backed by IndexedDB + local media. `prepare` runs once the local document is open and before the
 * editor mounts (P3: connect the live sync) and may return a cleanup; `chrome` renders over the editor.
 * `media` (server boards, P4) replaces the local-only images with remote ones and an upload queue.
 */
/** A local board's title (P5f): "All boards" above its name, and the same way back at the top of File. */
function LocalTitle({ title }: { title: string }) {
  const { t } = useTranslation();
  usePaletteCommands(() => [{ id: 'back', label: t('mainMenu.allBoards'), group: 'board', run: () => navigate('/') }]);
  const phone = useLayout() === 'phone';
  return <TopBar left={<TitlePill title={title} status="local" compact={phone} folder={{ label: t('board.allBoards'), to: '/' }} />} />;
}

export function BoardView({
  boardId,
  prepare,
  chrome,
  presence,
  comments,
  authors,
  media: mediaFor,
  links: linksFor,
  capabilities = LOCAL_CAPABILITIES,
  overlay,
  title,
  remoteHashes,
  checkAsset,
  figma,
  home,
}: {
  boardId: string;
  prepare?: (board: LocalBoard) => Promise<void | (() => void)>;
  chrome?: (board: LocalBoard) => ReactNode;
  presence?: (board: LocalBoard) => PresenceProps | null;
  /** Server boards: comment pins and threads (P7); local boards have none. */
  comments?: (board: LocalBoard) => CommentsProps | null;
  /** Server boards: who added each object (P5d). */
  authors?: (board: LocalBoard) => AuthorsSource | null;
  /** Server boards: link previews through the Worker, preview images stored as board assets. */
  links?: (session: BoardMediaSession) => LinkServices;
  media?: (board: LocalBoard, local: LocalMediaStore) => BoardMediaSession;
  /** Server boards: export and download rights of the viewer (spec §9.3). */
  capabilities?: Capabilities;
  /** Drawn over the canvas, under the chrome (the protected-link watermark). */
  overlay?: ReactNode;
  /** File name of exports (P9). */
  title?: string;
  /** Server boards: content hashes of the board's assets (`asset_hashes`), so an exported file deduplicates. */
  remoteHashes?: (ids: string[]) => Promise<Map<string, string>>;
  /** Server boards: an asset of the organization with this content hash, so an imported file reuses it. */
  checkAsset?: (sha256: string) => Promise<string | null>;
  /** Server boards, members (P11b): the Figma import proxy. */
  figma?: Pick<Api, 'figmaFile' | 'figmaImage'>;
  /** Where the brand mark leads (P16): all boards by default (local boards), `null` for none (guests by link). */
  home?: MainMenuHome | null;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ status: 'loading' });
  /** An imported image stored as a board asset: an asset of the organization with the same content is reused (P9). */
  const ingestFor =
    ({ session, local }: Extract<State, { status: 'ready' }>) =>
    async (blob: Blob, name: string, sha256: string | null): Promise<string | null> => {
      const hit = sha256 ? ((checkAsset ? await checkAsset(sha256).catch(() => null) : null) ?? local.findBySha(sha256)?.assetId ?? null) : null;
      if (hit) return hit;
      const f = new File([blob], name, { type: blob.type });
      return session ? session.importAsset(f) : ((await importAssets([f], local)).assets[0]?.assetId ?? null);
    };

  useEffect(() => {
    let alive = true;
    let opened: { board: LocalBoard; media: LocalMediaStore; session: BoardMediaSession | null; cleanup?: () => void } | null = null;
    setState({ status: 'loading' });
    (async () => {
      const media = await LocalMediaStore.open(undefined, { reducedMotion: prefersReducedMotion() });
      let board: LocalBoard;
      try {
        board = await openLocalBoard(boardId);
      } catch (err) {
        media.close();
        throw err;
      }
      let cleanup: (() => void) | undefined;
      try {
        cleanup = (await prepare?.(board)) ?? undefined;
      } catch (err) {
        board.destroy();
        media.close();
        throw err;
      }
      if (!alive) {
        cleanup?.();
        board.destroy();
        media.close();
        return;
      }
      const session = mediaFor?.(board, media) ?? null;
      opened = { board, media, session, cleanup };
      // E2E collaboration build only (VITE_E2E_STORE=1, never set for production): lets the spec drive the store.
      if (import.meta.env.VITE_E2E_STORE === '1') (window as unknown as { __osStore?: unknown }).__osStore = board.store;
      const onImport: ImportHandler = session?.onImport ?? ((files, at, adopt) => importImages(files, at, board.store, media, { adopt }));
      setState({ status: 'ready', board, media: session?.resolver ?? media, local: media, onImport, session, links: session && linksFor ? linksFor(session) : null });
    })().catch((error: unknown) => alive && setState({ status: 'error', error }));
    return () => {
      alive = false;
      opened?.cleanup?.();
      opened?.session?.destroy();
      opened?.board.destroy();
      opened?.media.close();
    };
  }, [boardId, prepare, mediaFor, linksFor]);

  if (state.status === 'loading') return <BoardSkeleton label={t('board.loading')} />;
  if (state.status === 'error') {
    const err = state.error instanceof BoardOpenError ? state.error : null;
    return (
      <div className="flex h-full items-center">
        <ErrorState title={t(`board.error.${err?.code ?? 'unknown'}`)}>
          {err && (
            <Button onClick={() => download(`board-${boardId}.json`, { boardId, exportedAt: new Date().toISOString(), objects: err.readable.objects })}>
              {t('board.exportJson')}
            </Button>
          )}
        </ErrorState>
      </div>
    );
  }
  return (
    <Restricted canDownload={capabilities.canDownload}>
      <TopBarProvider>
      <PaletteRegistry>
      <div className="relative h-full">
        {chrome?.(state.board)}
        <Editor
          key={boardId}
          store={state.board.store}
          media={state.media}
          onImport={state.onImport}
          onImportAsset={
            state.session?.onImportAsset ??
            (async (file) => {
              const { assets, failed } = await importAssets([file], state.local);
              return { asset: assets[0] ?? null, failed };
            })
          }
          presence={presence?.(state.board)}
          comments={comments?.(state.board)}
          authors={authors?.(state.board)}
          home={home === null ? undefined : (home ?? { to: '/', label: t('mainMenu.home') })}
          boardId={boardId}
          links={state.links}
          canCopyOut={capabilities.canExport}
          importFigma={figma ? { api: figma, boardId, ingest: ingestFor(state) } : undefined}
          importFile={async (file, at) => {
            const { importIntoBoard } = await import('../../features/export/import-here');
            return importIntoBoard({
              store: state.board.store,
              input: file,
              at,
              ingest: ingestFor(state),
            });
          }}
          exporter={exporterFor(capabilities, {
            title: title ?? t('export.untitled'),
            hashes: async (ids) => {
              const local = new Map(ids.flatMap((id) => (state.local.get(id)?.sha256 ? [[id, state.local.get(id)!.sha256] as [string, string]] : [])));
              const missing = ids.filter((id) => !local.has(id));
              const remote = remoteHashes && missing.length ? await remoteHashes(missing).catch(() => new Map<string, string>()) : new Map<string, string>();
              return new Map([...remote, ...local]);
            },
            meta: (id) => state.session?.resolver.info(id) ?? (state.local.get(id) ? { mime: state.local.get(id)!.mime, w: state.local.get(id)!.w, h: state.local.get(id)!.h } : null),
          })}
        />
        {overlay}
        {state.session && <UploadPanel queue={state.session.queue} />}
        {/* Local mode: the way home comes after the canvas in Tab order (the canvas is the first stop, as in P1). */}
        {!chrome && <LocalTitle title={title ?? t('titlePill.localBoard')} />}
      </div>
      </PaletteRegistry>
      </TopBarProvider>
    </Restricted>
  );
}
